import { MatchedIpa } from "../pattern/searchAndReplace";
import { Feature } from "../phonemes/features";
import { ipaToFeatures, IpaType } from "../phonemes/ipa";
import { applyRuleToWord } from "./applyRule";
import { CompiledRule } from "./compileRule";

/**
 * if a phoneme contains any of a set of diacritics,
 * rerun the rules without it, and replace it with a different diacritic
 */
export interface DiacriticRule {
  /** check if a phoneme contains any of these diacritics */
  readonly diacritics: IpaType[];
  /** replace diacritic with this */
  readonly to: IpaType;
  /** optionally prefix the modified phoneme with this */
  readonly toPre?: IpaType;
  /** optionally create a new segment */
  readonly newSegment?: IpaType;
  /** optionally split the segment on a diacritic */
  readonly split?: boolean;
  /** phoneme must either match given IPA or have given features */
  readonly mustBe?: IpaType | Feature[];
  /** also remove these diacritics */
  readonly remove?: IpaType[];
}

/**
 * for any phonemes that haven't been updated but contain specified diacritic,
 * remove diacritic, rerun rules, then optionally add back a different diacritic
 */
export function runDiacriticRule(
  matchedIn: MatchedIpa, rules: CompiledRule[], diacriticRule: DiacriticRule, subsetMatches?: boolean
): MatchedIpa {
  if (diacriticRule.split) {
    // note that 'split' ignores most parameters because they're currently not needed
    return splitSegments(matchedIn, rules, diacriticRule.diacritics, subsetMatches);
  }

  // check which phonemes both haven't yet been changed and contain a special diacritic
  const { diacritics, mustBe, remove } = diacriticRule;
  const affected1 = matchedIn.ipa.map((p, i) => {
    return !matchedIn.replaced[i] && diacritics.some(d => p.includes(d)) && isMatch(p, mustBe);
  });
  if (!affected1.some(a => a)) {
    return matchedIn; // nothing to do
  }

  // add segments if needed
  const [matched, affected] = addNewSegments(matchedIn, affected1, diacriticRule.newSegment);

  // remove diacritics
  matched.ipa = matched.ipa.map((p, i) => {
    if (affected[i]) {
      diacritics.forEach(d => { p = p.replace(d, '') });
      if (remove) {
        remove.forEach(d => { p = p.replace(d, '') });
      }
    }
    return p;
  });

  // rerun rules, only replacing IPA impacted by the diacritic rule
  // because otherwise it may do a subset match on symbols that need a later diacritic rule
  let affectedMatch = { ipa: matched.ipa, replaced: affected.map(r => !r) };
  rules.forEach(rule => { affectedMatch = applyRuleToWord(affectedMatch, rule, subsetMatches); });

  // if rules added segments, figure out how many extra segments per affected item
  const total = affected.reduce((prev, curr) => curr ? prev + 1 : prev, 0);
  const extra = total === 0 ? 0 : Math.floor((affectedMatch.ipa.length - matched.ipa.length) / total);

  // optional diacritics to add
  const pre = diacriticRule.toPre === undefined ? '' : diacriticRule.toPre;
  const post = diacriticRule.to;

  // figure out new 'matched'
  let ipa: IpaType[] = [];
  let replaced: boolean[] = [];
  if (extra === 0) {  // just updated affected segments
    ipa = affected.map((a, i) => a ? pre + affectedMatch.ipa[i] + post : matched.ipa[i]);
    replaced = affected.map((a, i) => a || matched.replaced[i]);
  } else {  // need to add segments
    for (let i = 0, j = 0; i < affected.length; i++, j++) {
      if (affected[i]) {
        for (let k = j; k <= j + extra; k++) {
          const segment = affectedMatch.ipa[k];
          ipa.push(k !== j + extra ? segment : pre + segment + post);
          replaced.push(true);
        }
        j += extra;
      } else {
        ipa.push(matched.ipa[i]);
        replaced.push(matched.replaced[i]);
      }
    }
  }
  return { ipa, replaced };
}

/** phoneme must either match a string or contain all the given features */
function isMatch(p: IpaType, mustBe?: IpaType | Feature[]): boolean {
  if (mustBe === undefined) {
    return true;
  } else if (typeof mustBe === 'string') {
    return p === mustBe;
  } else {
    const features = ipaToFeatures(p);
    return features !== undefined && mustBe.every(f => features.includes(f));
  }
}

/** any matching diacritic requires inserting items in 'matched' & 'affected' */
function addNewSegments(
  matched: MatchedIpa, affected: boolean[], newSegment?: string
): [MatchedIpa, boolean[]] {
  if (!newSegment) {
    return [matched, affected];
  }

  const ipa = matched.ipa.flatMap((p, i) => affected[i] ? [p, newSegment] : p);
  const replaced = matched.replaced.flatMap((r, i) => affected[i] ? [r, false] : r);
  const newAffected = affected.flatMap((a, i) => affected[i] ? [a, false] : a);
  return [{ ipa, replaced }, newAffected];
}

/** split segments on the diacritics & run rules on result */
function splitSegments(
  matchedIn: MatchedIpa, rules: CompiledRule[], diacritics: string[], subsetMatches?: boolean
): MatchedIpa {
  const affected = matchedIn.ipa.map((p, i) => {
    return !matchedIn.replaced[i] && diacritics.some(d => p.includes(d));
  });
  const ipa = matchedIn.ipa.flatMap((p, i) => affected[i] ? getSplit(p, diacritics) : p);
  const replaced = matchedIn.replaced.flatMap((r, i) => affected[i] ? [false, false] : r);
  const skip = !subsetMatches ? undefined
    : matchedIn.ipa.flatMap((_, i) => affected[i] ? [false, false] : true);

  let match: MatchedIpa = skip ? { ipa, replaced, skip } : { ipa, replaced };
  rules.forEach(rule => { match = applyRuleToWord(match, rule, subsetMatches); });
  return { ipa: match.ipa, replaced: match.replaced }; // strip off 'skip'
}

/** split a string in two if it has one of the diacritics in it */
function getSplit(p: string, diacritics: string[]): string | string[] {
  for (const d of diacritics) {
    const i = p.indexOf(d);
    if (i !== -1) {
      return [p.substring(0, i), p.substring(i + 1)];
    }
  }
  return p;
}
